import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Typography,
} from "antd";
import { useWatch } from "antd/lib/form/Form";
import { Trans, useTranslation } from "react-i18next";
import { TFunction } from "i18next";

import { AmountInput } from "@/modules/amount-input";
import { ExportSwitch } from "@/modules/recurring-fee/ExportSwitch";
import {
  UpsertTaxServiceInput,
  TaxServiceStatus,
  TaxServiceDepartment,
  TaxServiceName,
} from "../../../api/graphql/schema.generated";
import { TaxServiceModalContent } from "./styledComponent";
import {
  EXTERNAL_NOTE_TAX_SERVICES,
  FLAT_RATE_TAX_SERVICES,
  FREE_TAX_SERVICES,
  INTERNAL_NOTE_REQUIRED_SERVICES,
  PERIOD_REQUIRED_TAX_SERVICES,
  PICKABLE_TAX_SERVICE_DEPARTMENTS,
  QUANTITY_REQUIRED_TAX_SERVICES,
  TAX_SERVICE_NAMES_BY_DEPARTMENT,
  TAX_SERVICE_TAX_YEAR_OPTIONS,
  TAX_YEAR_REQUIRED_TAX_SERVICES,
  TIME_TRACKING_REQUIRED_TAX_SERVICES,
} from "./constants";
import {
  destroyMessage,
  eurosToCents,
  showGraphQlErrorNotification,
  showLoadingMessage,
} from "../../../utils";
import { useUpsertTaxServiceMutation } from "../../../api/graphql/mutations/taxService/upsertTaxService.generated";
import { IUser } from "../../../types";
import {
  externalCommentContainsUrl,
  externalCommentContainsCustomerName,
  isImplausiblyLowFlatRate,
} from "./invoiceConfirmationRules";

const { Paragraph, Text } = Typography;

const LOADING_MESSAGE_KEY = "saving-tax-service";

// Form-only fields for the From/To month span of a period service (SMT-1431).
// They are turned into periodStart and quantity before the confirmation step.
type PeriodFormValues = {
  periodFromMonth?: number | null;
  periodToMonth?: number | null;
};

// Form-only switch "In den Export übernehmen" (SMT-1434). It is turned into
// excludedFromExport before the confirmation step and never sent as such.
type ExportFormValues = {
  includeInExport?: boolean;
};

const getTexts = (t: TFunction<"ServiceTracker", undefined>) => ({
  title: (userName: string) => t("serviceTracker.modal.title", { userName }),
  ctaSave: t("serviceTracker.modal.ctaSave"),
  headline: t("serviceTracker.modal.headline"),
  optional: t(`serviceTracker.optional`),
  inputLabel: (inputName: string) =>
    t(`serviceTracker.modal.${inputName}Label`),
  inputPlaceholder: (inputName: string) =>
    t(`serviceTracker.modal.${inputName}Placeholder`),
  inputError: (inputName: string) =>
    t(`serviceTracker.modal.${inputName}Error`),
  departmentName: (name: string) => t(`serviceTracker.departmentsName.${name}`),
  serviceName: (name: string) => t(`serviceTracker.servicesName.${name}`),
  monthName: (month: number) => t(`serviceTracker.months.${month}`),
  status: (status: string) => t(`serviceTracker.statuses.${status}`),
  // Confirmation modal texts
  confirmationTitle: t("serviceTracker.modal.confirmation.title"),
  confirmationInvoiceCommentLabel: t(
    "serviceTracker.modal.confirmation.invoiceCommentLabel"
  ),
  confirmationEmptyComment: t("serviceTracker.modal.confirmation.emptyComment"),
  confirmationHintUrl: t("serviceTracker.modal.confirmation.hintUrl"),
  confirmationHintName: t("serviceTracker.modal.confirmation.hintName"),
  confirmationLowFlatRateWarning: t(
    "serviceTracker.modal.confirmation.lowFlatRateWarning"
  ),
  confirmationLowFlatRateCheckboxLabel: t(
    "serviceTracker.modal.confirmation.lowFlatRateCheckboxLabel"
  ),
  confirmationCtaConfirm: t("serviceTracker.modal.confirmation.ctaConfirm"),
  confirmationCtaBack: t("serviceTracker.modal.confirmation.ctaBack"),
});

const CreateTaxServiceModal = ({
  user,
  visible,
  onClose,
  onSuccess,
}: {
  user: IUser | null;
  visible: boolean;
  onClose: Function;
  onSuccess: Function;
}) => {
  const { t } = useTranslation("ServiceTracker");
  const texts = getTexts(t);

  const [form] = Form.useForm<
    UpsertTaxServiceInput & PeriodFormValues & ExportFormValues
  >();
  const [pendingSubmission, setPendingSubmission] =
    useState<UpsertTaxServiceInput | null>(null);
  const [acknowledgedLowFlatRate, setAcknowledgedLowFlatRate] = useState(false);

  const selectedDepartment = useWatch("department", form);
  const selectedService = useWatch("name", form);
  const periodFromMonth = useWatch("periodFromMonth", form);
  const periodToMonth = useWatch("periodToMonth", form);

  const isYearRequired = TAX_YEAR_REQUIRED_TAX_SERVICES.includes(
    selectedService!
  );

  const isPeriodRequired = PERIOD_REQUIRED_TAX_SERVICES.includes(
    selectedService!
  );

  // Number of months from the From to the To month, both included
  const monthCount =
    isPeriodRequired &&
    periodFromMonth &&
    periodToMonth &&
    periodToMonth >= periodFromMonth
      ? periodToMonth - periodFromMonth + 1
      : null;

  const isHoursRequired = TIME_TRACKING_REQUIRED_TAX_SERVICES.includes(
    selectedService!
  );

  const isQuantityRequired = QUANTITY_REQUIRED_TAX_SERVICES.includes(
    selectedService!
  );

  const isFreeTaxService = FREE_TAX_SERVICES.includes(selectedService!);

  const isFlatRateService = FLAT_RATE_TAX_SERVICES.includes(selectedService);

  const isExternalNoteRequired =
    EXTERNAL_NOTE_TAX_SERVICES.includes(selectedService);

  const isInternalNoteRequired =
    INTERNAL_NOTE_REQUIRED_SERVICES.includes(selectedService);

  const [upsertTaxService, { loading: isSaving }] =
    useUpsertTaxServiceMutation();

  const handleReset = useCallback(() => {
    form.resetFields();
  }, [form]);

  const handleCancel = () => {
    setPendingSubmission(null);
    setAcknowledgedLowFlatRate(false);
    onClose();
    handleReset();
  };

  const handleCancelConfirmation = () => {
    setPendingSubmission(null);
  };

  const handleConfirmSave = useCallback(async () => {
    if (!user || !pendingSubmission) {
      return;
    }
    showLoadingMessage(LOADING_MESSAGE_KEY);
    try {
      const values = pendingSubmission;
      await upsertTaxService({
        variables: {
          email: user.email,
          payload: {
            ...values,
            flatRate: values.flatRate ? eurosToCents(values.flatRate) : null,
            internalNote: values.internalNote ?? "",
            externalNote: values.externalNote ?? "",
            // Status is no longer chosen in the form (SMT-1434): every new
            // service is billable and only the export flag decides about EasyBill.
            status: TaxServiceStatus.WILL_BE_BILLED,
          },
        },
      });

      await onSuccess();

      onClose();
      handleReset();
      setPendingSubmission(null);
      setAcknowledgedLowFlatRate(false);
    } catch (error) {
      showGraphQlErrorNotification(`Saving tax service`, error);
    } finally {
      destroyMessage(LOADING_MESSAGE_KEY);
    }
  }, [
    handleReset,
    onClose,
    onSuccess,
    upsertTaxService,
    user,
    pendingSubmission,
  ]);

  const handleFormFinish = useCallback(
    (values: UpsertTaxServiceInput & PeriodFormValues & ExportFormValues) => {
      const { periodFromMonth, periodToMonth, includeInExport, ...submission } =
        values;
      // Free services have no charge, so the export flag is not sent for them.
      const exportPayload = FREE_TAX_SERVICES.includes(submission.name)
        ? {}
        : { excludedFromExport: !(includeInExport ?? true) };
      const payload = {
        ...submission,
        status: TaxServiceStatus.WILL_BE_BILLED,
        ...exportPayload,
      };
      if (PERIOD_REQUIRED_TAX_SERVICES.includes(submission.name)) {
        // The CUF is sent as its first month (from the tax year) and the month count
        setPendingSubmission({
          ...payload,
          periodStart: `${submission.year}-${String(periodFromMonth).padStart(
            2,
            "0"
          )}-01`,
          quantity: periodToMonth! - periodFromMonth! + 1,
        });
      } else {
        setPendingSubmission(payload);
      }
      setAcknowledgedLowFlatRate(false);
    },
    []
  );

  useEffect(() => {
    form.setFieldsValue({ name: undefined });
  }, [form, selectedDepartment]);

  useEffect(() => {
    form.setFieldsValue({
      year: null,
      hours: null,
      flatRate: null,
      periodFromMonth: null,
      periodToMonth: null,
      // Quantity defaults to 1 for the per-quantity services, cleared otherwise.
      quantity: QUANTITY_REQUIRED_TAX_SERVICES.includes(selectedService!)
        ? 1
        : null,
      // The export choice belongs to one service: a confirmed "off" must not
      // carry over to the next one. Free services are never exported (shown off).
      includeInExport: !FREE_TAX_SERVICES.includes(selectedService!),
    });
  }, [form, selectedService]);

  const departmentOptions = useMemo(() => {
    return PICKABLE_TAX_SERVICE_DEPARTMENTS.map((key) => ({
      value: key as TaxServiceDepartment,
      label: texts.departmentName(key),
    }));
  }, [texts]);

  const serviceNameOptions = useMemo(() => {
    return selectedDepartment
      ? TAX_SERVICE_NAMES_BY_DEPARTMENT[
          selectedDepartment as keyof typeof TAX_SERVICE_NAMES_BY_DEPARTMENT
        ]?.map((service) => ({
          value: service,
          label: texts.serviceName(service),
        }))
      : undefined;
  }, [selectedDepartment, texts]);

  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, index) => ({
      value: index + 1,
      label: texts.monthName(index + 1),
    }));
  }, [texts]);

  // Compute confirmation hints from pendingSubmission
  const hasUrlHint = pendingSubmission
    ? externalCommentContainsUrl(pendingSubmission.externalNote ?? "")
    : false;

  const hasNameHint =
    pendingSubmission && user
      ? externalCommentContainsCustomerName(
          pendingSubmission.externalNote ?? "",
          user
        )
      : false;

  const isLowFlatRate =
    pendingSubmission && pendingSubmission.name
      ? FLAT_RATE_TAX_SERVICES.includes(pendingSubmission.name) &&
        pendingSubmission.flatRate !== null &&
        pendingSubmission.flatRate !== undefined &&
        isImplausiblyLowFlatRate(pendingSubmission.flatRate)
      : false;

  const footer = [
    <Button
      form="taxServiceFormId"
      key="submit"
      htmlType="submit"
      type="primary"
      loading={isSaving}
    >
      {texts.ctaSave}
    </Button>,
  ];

  if (!user) {
    return null;
  }

  // Round the input hours value to a multiple of the step:
  // payroll consulting is billed in 0.5h increments, all other services in 0.25h.
  // valid values (0.25 step): 0,25 0,5, 0,75, 1, 1,25 ...
  const handleBlurHoursInput = () => {
    const roundingStep =
      selectedService === TaxServiceName.PAYROLL_CONSULTING ? 0.5 : 0.25;
    const hours: number = form.getFieldValue("hours");
    let roundValue = hours - (hours % roundingStep);
    if (roundValue === 0) {
      roundValue = roundingStep;
    }
    form.setFieldsValue({ hours: roundValue });
  };

  return (
    <>
      <Modal
        title={texts.title(`${user.firstName} ${user.lastName}`)}
        visible={visible}
        onCancel={handleCancel}
        footer={footer}
        centered
        width={600}
      >
        <Form
          form={form}
          name="taxServiceForm"
          id="taxServiceFormId"
          onFinish={handleFormFinish}
          autoComplete="off"
          layout="vertical"
          requiredMark={false}
        >
          <TaxServiceModalContent>
            <Form.Item
              name="department"
              label={texts.inputLabel("selectField")}
              rules={[{ required: true }]}
            >
              <Select
                options={departmentOptions}
                placeholder={texts.inputPlaceholder("selectField")}
              ></Select>
            </Form.Item>
            <Form.Item
              name="name"
              label={texts.inputLabel("selectService")}
              rules={[{ required: true }]}
            >
              <Select
                options={serviceNameOptions}
                placeholder={texts.inputPlaceholder("selectService")}
              ></Select>
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.name !== currentValues.name
              }
            >
              <Row gutter={10} justify="space-between">
                <Col span={isPeriodRequired ? 6 : 12}>
                  <Form.Item
                    name="year"
                    label={texts.inputLabel("selectTaxYear")}
                    rules={[
                      {
                        required: isYearRequired,
                      },
                    ]}
                  >
                    <Select
                      options={TAX_SERVICE_TAX_YEAR_OPTIONS}
                      disabled={!isYearRequired}
                    ></Select>
                  </Form.Item>
                </Col>
                {isPeriodRequired ? (
                  <>
                    <Col span={6}>
                      <Form.Item
                        name="periodFromMonth"
                        label={texts.inputLabel("selectMonthFrom")}
                        rules={[
                          {
                            required: isPeriodRequired,
                            message: texts.inputError("selectMonthFrom"),
                          },
                        ]}
                      >
                        <Select
                          options={monthOptions}
                          placeholder={texts.inputPlaceholder(
                            "selectMonthFrom"
                          )}
                        ></Select>
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        name="periodToMonth"
                        dependencies={["periodFromMonth"]}
                        label={texts.inputLabel("selectMonthTo")}
                        rules={[
                          {
                            required: isPeriodRequired,
                            message: texts.inputError("selectMonthTo"),
                          },
                          ({ getFieldValue }) => ({
                            validator(_, toMonth: number | null) {
                              const fromMonth =
                                getFieldValue("periodFromMonth");
                              if (toMonth && fromMonth && toMonth < fromMonth) {
                                return Promise.reject(
                                  new Error(
                                    texts.inputError("selectMonthToBeforeFrom")
                                  )
                                );
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Select
                          options={monthOptions}
                          placeholder={texts.inputPlaceholder("selectMonthTo")}
                        ></Select>
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label={texts.inputLabel("inputMonthCount")}>
                        <Input disabled value={monthCount ?? ""} />
                      </Form.Item>
                    </Col>
                  </>
                ) : (
                  <Col span={12}>
                    {isFlatRateService ? (
                      <Form.Item
                        name="flatRate"
                        label={texts.inputLabel("inputFlatRate")}
                        rules={[
                          {
                            required: isFlatRateService,
                            validator: (_, flatRate: number) => {
                              if (
                                isFlatRateService &&
                                (flatRate === null || flatRate <= 0)
                              ) {
                                return Promise.reject(
                                  new Error(texts.inputError("inputFlatRate"))
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <AmountInput
                          addonAfter="€"
                          allowNegative
                          style={{ width: "100%" }}
                          disabled={!isFlatRateService}
                          placeholder={texts.inputPlaceholder("inputFlatRate")}
                        />
                      </Form.Item>
                    ) : isQuantityRequired ? (
                      <Form.Item
                        name="quantity"
                        label={texts.inputLabel("inputQuantity")}
                        rules={[
                          {
                            required: isQuantityRequired,
                            validator: (_, quantity: number) => {
                              if (
                                isQuantityRequired &&
                                (quantity === null ||
                                  !Number.isInteger(quantity) ||
                                  quantity < 1)
                              ) {
                                return Promise.reject(
                                  new Error(texts.inputError("inputQuantity"))
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber
                          type="number"
                          min={1}
                          step={1}
                          precision={0}
                          controls={false}
                          style={{ width: "100%" }}
                          placeholder={texts.inputPlaceholder("inputQuantity")}
                        />
                      </Form.Item>
                    ) : (
                      <Form.Item
                        name="hours"
                        label={texts.inputLabel("inputHours")}
                        rules={[
                          {
                            required: isHoursRequired,
                            validator: (_, amount: number) => {
                              if (
                                isHoursRequired &&
                                (amount === 0 || amount === null)
                              ) {
                                return Promise.reject(
                                  new Error(texts.inputError("inputHours"))
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber
                          type="number"
                          controls={false}
                          style={{ width: "100%" }}
                          disabled={!isHoursRequired}
                          onBlur={handleBlurHoursInput}
                          placeholder={texts.inputPlaceholder("inputHours")}
                        />
                      </Form.Item>
                    )}
                  </Col>
                )}
              </Row>
            </Form.Item>
            <Form.Item
              name="internalNote"
              label={
                <Paragraph>
                  <Text>{texts.inputLabel("inputInternalComment")}</Text>
                  {!isInternalNoteRequired && (
                    <Text type="secondary" style={{ marginLeft: "8px" }}>
                      {texts.optional}
                    </Text>
                  )}
                </Paragraph>
              }
              rules={[{ required: isInternalNoteRequired }]}
            >
              <Input.TextArea
                showCount
                maxLength={250}
                defaultValue=""
                placeholder={texts.inputPlaceholder("inputInternalComment")}
              />
            </Form.Item>
            <Form.Item
              name="externalNote"
              label={
                <Paragraph>
                  <Text>
                    <Trans
                      defaults={texts.inputLabel("inputCommentForClient")}
                      components={[<b key="bold" />]}
                    />
                  </Text>
                  {!isExternalNoteRequired && (
                    <Text type="secondary" style={{ marginLeft: "8px" }}>
                      {texts.optional}
                    </Text>
                  )}
                </Paragraph>
              }
              rules={[{ required: isExternalNoteRequired }]}
            >
              <Input.TextArea
                showCount
                maxLength={255}
                defaultValue=""
                placeholder={texts.inputPlaceholder("inputCommentForClient")}
              />
            </Form.Item>
            <Form.Item
              label={texts.inputLabel("includeInExport")}
              name="includeInExport"
              valuePropName="checked"
              initialValue={true}
            >
              <ExportSwitch
                confirmOnDisable
                disabled={isFreeTaxService}
                data-test="includeInExportSwitch"
              />
            </Form.Item>
          </TaxServiceModalContent>
        </Form>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        title={texts.confirmationTitle}
        visible={pendingSubmission !== null}
        onCancel={handleCancelConfirmation}
        centered
        width={600}
        footer={[
          <Button
            key="back"
            onClick={handleCancelConfirmation}
            data-test="confirmationBack"
          >
            {texts.confirmationCtaBack}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={isSaving}
            onClick={handleConfirmSave}
            disabled={isLowFlatRate && !acknowledgedLowFlatRate}
            data-test="confirmationConfirm"
          >
            {texts.confirmationCtaConfirm}
          </Button>,
        ]}
      >
        <div style={{ marginBottom: "16px" }}>
          <Typography.Text strong>
            {texts.confirmationInvoiceCommentLabel}
          </Typography.Text>
          <div
            data-test="confirmationQuotedComment"
            style={{
              marginTop: "8px",
              padding: "12px",
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
              minHeight: "40px",
            }}
          >
            {pendingSubmission?.externalNote?.trim() ? (
              <Typography.Text>
                {pendingSubmission.externalNote}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">
                {texts.confirmationEmptyComment}
              </Typography.Text>
            )}
          </div>
        </div>

        {hasUrlHint && (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px",
              backgroundColor: "#e6f7ff",
              borderRadius: "4px",
            }}
          >
            <Typography.Text>{texts.confirmationHintUrl}</Typography.Text>
          </div>
        )}

        {hasNameHint && (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px",
              backgroundColor: "#e6f7ff",
              borderRadius: "4px",
            }}
          >
            <Typography.Text>{texts.confirmationHintName}</Typography.Text>
          </div>
        )}

        {isLowFlatRate && (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px",
              backgroundColor: "#fff7e6",
              borderRadius: "4px",
            }}
          >
            <div style={{ marginBottom: "12px" }}>
              <Typography.Text>
                {texts.confirmationLowFlatRateWarning}
              </Typography.Text>
            </div>
            <Checkbox
              data-test="confirmationAcknowledgeLowFlatRate"
              checked={acknowledgedLowFlatRate}
              onChange={(e) => setAcknowledgedLowFlatRate(e.target.checked)}
            >
              {texts.confirmationLowFlatRateCheckboxLabel}
            </Checkbox>
          </div>
        )}
      </Modal>
    </>
  );
};

export default CreateTaxServiceModal;
